import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import {
  AnalyticsService,
  PaginationComponent,
  ResponsiveService,
  InfoItem,
} from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import { DistributionHistory } from '@products/models/distribution-history';
import {
  ConfigurationId,
  DistributionTableConfig,
  EDSPaginationPageChangedEvent,
  FundId,
  DistributionLatestConfig,
  PaginationActionEnum,
  ProductType,
} from '@types';
import { Title } from '@angular/platform-browser';
import { TableResponsiveService } from '@shared/table/table-responsive.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { DistributionService } from '@products/pricing-distribution/services/distribution.service';
import { generateUUID } from '@utils/text/string-utils';
interface YearOption {
  label: string;
  value: string;
}

@Component({
  selector: 'ft-distribution-historical',
  templateUrl: './distribution-historical.component.html',
  styleUrls: ['./distribution-historical.component.scss'],
  providers: [TableResponsiveService],
})
export class DistributionHistoricalComponent
  extends AbstractBaseComponent
  implements OnInit, OnChanges, OnDestroy {
  isHandheld$: Observable<boolean>;
  @Input() latestConfig: DistributionLatestConfig;
  @Input() tableConfig: DistributionTableConfig;
  @Input() filteredHistoricalList: DistributionHistory[];
  @Input() historicalTableTotalPages: {
    count: number;
  };
  @Input() fundId: FundId;
  @Input() isSiteIntl: boolean;
  @Input() configurationName: ConfigurationId;
  @Input() productType: ProductType;
  @Output() selectedYear = new EventEmitter<string>();
  @Output() selectedPage = new EventEmitter<number>();
  @Output() downloadAction = new EventEmitter();
  historicalList: DistributionHistory[] = [];
  totalPages: number;
  yearOptionList: YearOption[] = [];
  yearOptionSelected: string;
  startDate: string;
  endDate: string;
  isSgHk: false;
  showCanadaDistForEtfMfMmf: boolean;
  tooltips: { [name: string]: string } = {};
  hideExDistDate: boolean;

  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  @ViewChild('scroll') scrollPoint: ElementRef;
  cards: InfoItem[][] = [];
  pageTitle: string;
  @ViewChild('tableResponsive', { static: false })
  public set table(table: ElementRef) {
    if (table) {
      this.tableResponsiveService?.init(table);
    }
  }

  public id = `pricingChartDropDown${generateUUID()}`;

  public productConfigurationType = ConfigurationId;

  private unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private analytics: AnalyticsService,
    private titleService: Title,
    protected tableResponsiveService: TableResponsiveService,
    private changeDetectorRef: ChangeDetectorRef,
    private distributionService: DistributionService
  ) {
    super();
    this.tableResponsiveService.cards$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cards = cards;
        this.changeDetectorRef.detectChanges();
      });
  }
  ngOnInit(): void {
    super.ngOnInit();
    this.showCanadaDistForEtfMfMmf = this.distributionService.showCanadaDistForEtfMfMmf(
      this.productType
    );
    this.pageTitle = this.titleService.getTitle();
    this.hideExDistDate = this.distributionService.hideExDistDateForCountry();
    this.tableConfig.dividendYears.map((year) => {
      this.yearOptionList.push({
        label:
          year === 'SINCE_INCEPTION'
            ? this.translateService.instant(
                'products.distribution-SINCE_INCEPTION'
              )
            : year,
        value: year,
      });
    });
    this.populateDistributionState(this.tableConfig);
    this.historicalList = this.addIncomeOverCapGainsFlag(
      this.filteredHistoricalList
    );
    this.totalPages = this.historicalTableTotalPages.count;
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.changeDetectorRef.detectChanges();
    this.setTooltips();
  }

  /**
   * Adds a flag based on DistributionExDividendDate
   * @param historicalList list
   * @returns jformatted list with flag
   */
  private addIncomeOverCapGainsFlag(
    historicalList: DistributionHistory[]
  ): DistributionHistory[] {
    historicalList?.forEach((element) => {
      element.showIncomeFlag = element.incomeDistributionExDividendDate
        ? true
        : false;
    });
    return historicalList;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableConfig && !changes.tableConfig.firstChange) {
      // avoid duplicate trigger of populate method on first load.
      this.populateDistributionState(changes.tableConfig.currentValue);
    }

    if (
      changes.filteredHistoricalList &&
      !changes.filteredHistoricalList.firstChange
    ) {
      this.historicalList = this.addIncomeOverCapGainsFlag(
        changes.filteredHistoricalList.currentValue
      );

      // Scroll the view, when listing is changed.
      if (this.scrollPoint) {
        // TODO: refactor scrolling to use WindowScrollService
        const element = this.scrollPoint.nativeElement;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    if (
      changes.historicalTableTotalPages &&
      !changes.historicalTableTotalPages.firstChange
    ) {
      this.totalPages = changes.historicalTableTotalPages.currentValue.count;
      this.paginationComponent.activePage = 1;
      this.paginationComponent?.updatePagination(this.totalPages); // Perform changes on pagination component.
    }
  }

  private setTooltips() {
    this.tooltips[
      'products.distributable-income-column-header'
    ] = this.translateService.tooltip(
      'products.distributable-income-column-header'
    );
    this.tooltips[
      'products.dividend-income-column-header'
    ] = this.translateService.tooltip('products.dividend-income-column-header');
  }

  /**
   * Initializes properties on page load.
   * Again, called when 'input' changes.
   */
  populateDistributionState(tableConfig: DistributionTableConfig): void {
    this.yearOptionSelected = tableConfig.selectedYear;
    this.startDate = tableConfig.startDate;
    this.endDate = tableConfig.endDate;
    this.setTooltips();
  }

  /**
   * Return the title text for the option selected.
   * @param yearOptionSelected year option selected, coming from state
   */
  getTitleText(yearOptionSelected: string): string {
    return this.yearOptionList.find(
      (yearoption) => yearoption.value === yearOptionSelected
    )?.label;
  }

  /**
   * Update distribution table on year change.
   * @param selectedOption distribution year option selected
   */
  onDistributionYearChanged(selectedOption: YearOption): void {
    this.selectedYear.next(selectedOption.value);
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  onPageChanged(event: EDSPaginationPageChangedEvent): void {
    if (event.action !== 'load') {
      this.selectedPage.next(event.page);
    }
  }

  /**
   * Trigger download action.
   */
  onDownloadButtonClicked(): void {
    this.downloadAction.next();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
